/**
 * Mobile Tool Pages — Dashboard, Ops Log, Fleet, Quote, ROI
 * All dark-themed, mobile-first. Authenticated via global authFetch (Bearer
 * token from /m passcode unlock).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  black: '#FFFFFF', dark: '#FAFAFA', panel: '#FFFFFF', card: '#FFFFFF',
  border: '#E5E5E5', red: '#CC0000', gold: '#A8862F', green: '#15803D',
  amber: '#B45309', white: '#0A0A0A', light: '#525252', dim: '#A3A3A3',
};

const fmt$ = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmt$2 = (n) => `$${Number(n || 0).toFixed(2).toLocaleString()}`;

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, padding: 14, ...style }}>{children}</div>
);

const SectionHead = ({ children, accent = T.red }) => (
  <p style={{ fontSize: 9, color: accent, textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>{children}</p>
);

const Stat = ({ label, value, color = T.white }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, padding: '10px 6px', textAlign: 'center' }}>
    <p style={{ fontSize: 8, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, margin: 0 }}>{label}</p>
    <p style={{ fontSize: 13, fontWeight: 900, color, margin: '3px 0 0', fontFamily: 'DM Mono, monospace', fontVariantNumeric: 'tabular-nums' }}>{value}</p>
  </div>
);

const inputStyle = (extra = {}) => ({
  background: T.dark, border: `1px solid ${T.border}`, color: T.white,
  padding: '10px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', width: '100%', ...extra,
});

const Field = ({ label, children }) => (
  <div>
    <label style={{ fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, display: 'block', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

// ──────────────────────────────────────────────────────────────────────
// 1. Dashboard — 7-Pillar P&L summary (read-only)
// ──────────────────────────────────────────────────────────────────────
export function MobileDashboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/command-center/pnl/7-pillar?year=${year}&month=${month}`);
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch (e) { toast.error('Failed to load dashboard'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [year, month]);

  const maxRev = useMemo(() => Math.max(1, ...(data?.pillars || []).map(p => p.revenue || 0)), [data]);

  return (
    <div data-testid="mobile-dashboard">
      <SectionHead>Command Dashboard · 7-Pillar P&amp;L</SectionHead>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <select value={month} onChange={e => setMonth(Number(e.target.value))} style={inputStyle({ flex: 1 })} data-testid="dash-month">
          {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => <option key={m} value={i + 1} style={{ background: T.dark }}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={inputStyle({ width: 90 })} data-testid="dash-year">
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y} style={{ background: T.dark }}>{y}</option>)}
        </select>
      </div>

      {loading && <p style={{ color: T.dim, fontSize: 12, textAlign: 'center', padding: 30 }} data-testid="dash-loading">Loading…</p>}

      {!loading && data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 12 }}>
            <Stat label="Revenue" value={fmt$(data.total_revenue)} color={T.gold} />
            <Stat label="Costs"   value={fmt$(data.total_costs)} color={T.amber} />
            <Stat label="Net"     value={fmt$(data.total_net)} color={data.total_net >= 0 ? T.green : T.red} />
          </div>

          <Card style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 4px' }}>Top Pillar</p>
            <p style={{ fontSize: 16, fontWeight: 900, color: T.white, margin: 0 }}>{data.highest_revenue_pillar || '—'}</p>
            <p style={{ fontSize: 10, color: T.light, margin: '4px 0 0' }}>Best margin: <span style={{ color: T.gold, fontWeight: 700 }}>{data.highest_margin_pillar || '—'}</span> · Overall {data.total_margin_pct}%</p>
          </Card>

          <SectionHead accent={T.gold}>Pillars</SectionHead>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="dash-pillars">
            {(data.pillars || []).map(p => (
              <Card key={p.key} style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <p style={{ fontSize: 12, fontWeight: 800, color: T.white, margin: 0 }}>{p.label}</p>
                  <p style={{ fontSize: 12, color: p.net >= 0 ? T.green : T.red, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{fmt$(p.net)}</p>
                </div>
                <div style={{ height: 6, background: T.dark, marginTop: 6, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, (p.revenue / maxRev) * 100)}%`, background: p.color || T.red }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ fontSize: 10, color: T.dim }}>Rev {fmt$(p.revenue)} · Cost {fmt$(p.costs)}</span>
                  <span style={{ fontSize: 10, color: T.light, fontFamily: 'DM Mono, monospace' }}>{p.margin_pct}%</span>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 2. Ops Log v3 — Daily P&L + Members + A/R Aging (read-only)
// ──────────────────────────────────────────────────────────────────────
export function MobileOpsLog() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [tab, setTab] = useState('pl');
  const [pl, setPL] = useState(null);
  const [members, setMembers] = useState([]);
  const [ar, setAr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [plR, memR, arR] = await Promise.all([
          fetch(`${API}/api/command-center/ops/daily-pl?date=${date}`).then(r => r.json()),
          fetch(`${API}/api/command-center/ops/members`).then(r => r.json()),
          fetch(`${API}/api/command-center/ops/ar-aging`).then(r => r.json()),
        ]);
        if (cancelled) return;
        setPL(plR); setMembers(memR.members || memR || []); setAr(arR);
      } catch (e) { toast.error('Failed to load ops data'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [date]);

  return (
    <div data-testid="mobile-ops">
      <SectionHead>Ops Log v3</SectionHead>
      <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle({ marginBottom: 12 })} data-testid="ops-date" />

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, borderBottom: `1px solid ${T.border}` }}>
        {[{ id: 'pl', label: 'Daily P&L' }, { id: 'members', label: 'Members' }, { id: 'ar', label: 'A/R Aging' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 0, color: tab === t.id ? T.red : T.light,
            borderBottom: `2px solid ${tab === t.id ? T.red : 'transparent'}`,
            padding: '8px 10px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer',
          }} data-testid={`ops-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {loading && <p style={{ color: T.dim, fontSize: 12, textAlign: 'center', padding: 30 }} data-testid="ops-loading">Loading…</p>}

      {!loading && tab === 'pl' && pl && (
        <div data-testid="ops-pl-panel">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 8 }}>
            <Stat label="Bookings" value={pl.bookings_count || 0} />
            <Stat label="Hours" value={(pl.total_hours || 0).toFixed(1)} />
            <Stat label="Gross" value={fmt$(pl.gross)} color={T.gold} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 8 }}>
            <Stat label="Drivers" value={fmt$(pl.driver_pay)} color={T.amber} />
            <Stat label="Fuel" value={fmt$(pl.fuel)} color={T.amber} />
            <Stat label="Net" value={fmt$(pl.net)} color={pl.net >= 0 ? T.green : T.red} />
          </div>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em' }}>ACE Keep</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: T.green, fontFamily: 'DM Mono, monospace' }}>{fmt$(pl.ace_keep)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em' }}>Tax Reserve</span>
              <span style={{ fontSize: 12, color: T.amber, fontFamily: 'DM Mono, monospace' }}>{fmt$(pl.tax_reserve)}</span>
            </div>
          </Card>
          <p style={{ fontSize: 10, color: T.dim, margin: '14px 0 6px', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Bookings ({(pl.bookings || []).length})</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(pl.bookings || []).map((b, i) => (
              <Card key={b.booking_id || i} style={{ padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: T.white, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.client_name || 'Walk-in'}</p>
                    <p style={{ fontSize: 10, color: T.light, margin: '2px 0 0' }}>{b.vehicle || '—'} · {b.hours || 0}h</p>
                  </div>
                  <span style={{ fontSize: 12, color: T.gold, fontFamily: 'DM Mono, monospace', fontWeight: 700 }}>{fmt$(b.gross || b.total)}</span>
                </div>
              </Card>
            ))}
            {(pl.bookings || []).length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', color: T.dim, fontSize: 11, border: `1px dashed ${T.border}` }}>No bookings on this day.</div>
            )}
          </div>
        </div>
      )}

      {!loading && tab === 'members' && (
        <div data-testid="ops-members-panel" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(members || []).map(m => (
            <Card key={m.member_id || m.id} style={{ padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: T.white, margin: 0 }}>{m.name}</p>
                  <p style={{ fontSize: 10, color: T.light, margin: '2px 0 0', textTransform: 'capitalize' }}>{m.tier}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: 11, color: T.gold, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{fmt$(m.tier_meta?.monthly_fee)}/mo</p>
                  {m.hours_remaining !== undefined && m.hours_remaining !== null && (
                    <p style={{ fontSize: 10, color: m.hours_remaining > 0 ? T.green : T.dim, margin: '2px 0 0', fontFamily: 'DM Mono, monospace' }}>{m.hours_remaining}h left</p>
                  )}
                </div>
              </div>
            </Card>
          ))}
          {(members || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: T.dim, fontSize: 11, border: `1px dashed ${T.border}` }}>No active members.</div>}
        </div>
      )}

      {!loading && tab === 'ar' && ar && (
        <div data-testid="ops-ar-panel">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 12 }}>
            <Stat label="Outstanding" value={fmt$(ar.outstanding)} color={T.amber} />
            <Stat label="Overdue" value={fmt$(ar.overdue)} color={T.red} />
            <Stat label="Collected" value={fmt$(ar.collected)} color={T.green} />
          </div>
          <p style={{ fontSize: 10, color: T.dim, margin: '4px 0 6px', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Invoices ({(ar.invoices || []).length})</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(ar.invoices || []).map((iv, i) => (
              <Card key={iv.invoice_id || i} style={{ padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, color: T.white, margin: 0 }}>{iv.bill_to || iv.client_name || '—'}</p>
                    <p style={{ fontSize: 10, color: T.light, margin: '2px 0 0' }}>{iv.invoice_id} · <span style={{ color: iv.status === 'paid' ? T.green : (iv._aging_bucket === 'overdue' ? T.red : T.amber) }}>{iv._aging_bucket || iv.status}</span></p>
                  </div>
                  <span style={{ fontSize: 12, color: T.gold, fontFamily: 'DM Mono, monospace', fontWeight: 700 }}>{fmt$(iv.subtotal || iv.total)}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 3. Fleet Manager — vehicle list + tap-to-edit pricing
// ──────────────────────────────────────────────────────────────────────
export function MobileFleet() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pricing/vehicles`);
        const d = await r.json();
        if (!cancelled) setVehicles(d.vehicles || []);
      } catch (e) { toast.error('Failed to load fleet'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const save = async (vehicleId, patch) => {
    try {
      const r = await fetch(`${API}/api/pricing/vehicles/${vehicleId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      toast.success('Vehicle updated');
      setEditing(null);
      setReloadTick(t => t + 1);
    } catch (e) { toast.error(e.message); }
  };

  if (editing) return <FleetEditor vehicle={editing} onCancel={() => setEditing(null)} onSave={(patch) => save(editing.vehicle_id, patch)} />;

  return (
    <div data-testid="mobile-fleet">
      <SectionHead>Fleet Manager</SectionHead>
      {loading && <p style={{ color: T.dim, fontSize: 12, textAlign: 'center', padding: 30 }} data-testid="fleet-loading">Loading…</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {vehicles.map(v => (
          <button key={v.vehicle_id} onClick={() => setEditing(v)}
            style={{ background: T.card, border: `1px solid ${T.border}`, padding: 12, textAlign: 'left', cursor: 'pointer', color: T.white }}
            data-testid={`fleet-row-${v.vehicle_id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: T.white, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.vehicle_name}</p>
                <p style={{ fontSize: 10, color: T.light, margin: '2px 0 0', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{v.category} · {v.pax || '—'} pax · {v.min_hours || 0}h min</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 11, color: T.gold, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{fmt$(v.retail_hourly)}/hr</p>
                <p style={{ fontSize: 9, color: T.dim, margin: '2px 0 0' }}>ROI {v.roi_percent || 0}%</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const FleetEditor = ({ vehicle, onCancel, onSave }) => {
  const [draft, setDraft] = useState({
    vehicle_name: vehicle.vehicle_name || '',
    retail_hourly: vehicle.retail_hourly || 0,
    member_hourly: vehicle.member_hourly || 0,
    affiliate_hourly: vehicle.affiliate_hourly || 0,
    rental_low: vehicle.rental_low || 0,
    rental_high: vehicle.rental_high || 0,
    min_hours: vehicle.min_hours || 0,
    pax: vehicle.pax || '',
    tag: vehicle.tag || '',
  });

  return (
    <div data-testid="fleet-editor">
      <button onClick={onCancel} style={{ background: 'none', border: 0, color: T.red, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', padding: '0 0 12px' }} data-testid="fleet-editor-back">← Fleet</button>
      <SectionHead>Edit · {vehicle.vehicle_name}</SectionHead>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Field label="Vehicle Name"><input value={draft.vehicle_name} onChange={e => setDraft({ ...draft, vehicle_name: e.target.value })} style={inputStyle()} data-testid="fleet-name" /></Field>
        <Field label="Retail $/hr"><input type="number" value={draft.retail_hourly} onChange={e => setDraft({ ...draft, retail_hourly: Number(e.target.value) })} style={inputStyle()} data-testid="fleet-retail" /></Field>
        <Field label="Member $/hr"><input type="number" value={draft.member_hourly} onChange={e => setDraft({ ...draft, member_hourly: Number(e.target.value) })} style={inputStyle()} data-testid="fleet-member" /></Field>
        <Field label="Affiliate $/hr"><input type="number" value={draft.affiliate_hourly} onChange={e => setDraft({ ...draft, affiliate_hourly: Number(e.target.value) })} style={inputStyle()} data-testid="fleet-affiliate" /></Field>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1 }}><Field label="Rental Low"><input type="number" value={draft.rental_low} onChange={e => setDraft({ ...draft, rental_low: Number(e.target.value) })} style={inputStyle()} data-testid="fleet-rental-low" /></Field></div>
          <div style={{ flex: 1 }}><Field label="Rental High"><input type="number" value={draft.rental_high} onChange={e => setDraft({ ...draft, rental_high: Number(e.target.value) })} style={inputStyle()} data-testid="fleet-rental-high" /></Field></div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1 }}><Field label="Min Hours"><input type="number" value={draft.min_hours} onChange={e => setDraft({ ...draft, min_hours: Number(e.target.value) })} style={inputStyle()} data-testid="fleet-min-hours" /></Field></div>
          <div style={{ flex: 1 }}><Field label="Pax"><input value={draft.pax} onChange={e => setDraft({ ...draft, pax: e.target.value })} style={inputStyle()} data-testid="fleet-pax" /></Field></div>
        </div>
        <Field label="Tag"><input value={draft.tag} onChange={e => setDraft({ ...draft, tag: e.target.value })} placeholder="featured · new · etc." style={inputStyle()} data-testid="fleet-tag" /></Field>

        <button onClick={() => onSave(draft)} style={{ background: T.red, color: '#FFFFFF', border: 0, padding: '14px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', marginTop: 8 }} data-testid="fleet-save">Save Changes</button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// 4. Quote Builder — calculator that logs to backend
// ──────────────────────────────────────────────────────────────────────
export function MobileQuote() {
  const [rateCard, setRateCard] = useState({});
  const [draft, setDraft] = useState({ vehicle_type: '', hours: 4, pricing_type: 'retail', driver_rate: 30, rental_cost: 0, is_rental: false });
  const [client, setClient] = useState({ name: '', phone: '', email: '' });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [booking, setBooking] = useState({ event_date: '', pickup_time: '', pickup_location: '', dropoff_location: '', notes: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pricing/quote/rate-card`);
        const d = await r.json();
        if (cancelled) return;
        setRateCard(d || {});
        const firstKey = Object.keys(d || {})[0];
        if (firstKey) setDraft(prev => ({ ...prev, vehicle_type: firstKey }));
      } catch (e) { toast.error('Failed to load rate card'); }
    })();
    return () => { cancelled = true; };
  }, []);

  const calculate = async () => {
    if (!draft.vehicle_type) { toast.error('Pick a vehicle'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/pricing/quote/calculate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft),
      });
      if (!r.ok) throw new Error('Calculation failed');
      const d = await r.json();
      setQuote(d);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const logQuote = async () => {
    if (!quote) return;
    if (!client.name) { toast.error('Add client name first'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/command-center/quote-log`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: client.name, client_phone: client.phone, client_email: client.email,
          service_type: quote.vehicle, vehicle: quote.vehicle, hours: quote.effective_hours,
          subtotal: quote.revenue, total: quote.revenue, status: 'sent',
        }),
      });
      if (!r.ok) throw new Error('Failed to log quote');
      const d = await r.json();
      toast.success(`Quote ${d.quote_id} logged`);
      setClient({ name: '', phone: '', email: '' });
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const smsQuote = async () => {
    if (!quote) return;
    if (!client.phone.trim()) { toast.error('Add a phone number first'); return; }
    setBusy(true);
    try {
      const lines = [
        client.name ? `Hi ${client.name.split(' ')[0]},` : 'Hi,',
        '',
        `Your ACE quote:`,
        `${quote.vehicle}`,
        `${quote.effective_hours}h @ $${quote.applied_rate}/hr`,
        `Total: $${Number(quote.revenue).toFixed(2)}`,
        '',
        `Reply YES to book or call (470) 580-1985.`,
        `— Auto Concierge Enterprises`,
      ];
      const r = await fetch(`${API}/api/command-center/quo/send-sms`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: client.phone, message: lines.join('\n') }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.detail || 'SMS not sent — check OpenPhone config');
      toast.success(`SMS sent to ${client.phone}`);
      // Also log the quote silently
      fetch(`${API}/api/command-center/quote-log`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: client.name || '(SMS)', client_phone: client.phone, client_email: client.email,
          service_type: quote.vehicle, vehicle: quote.vehicle, hours: quote.effective_hours,
          subtotal: quote.revenue, total: quote.revenue, status: 'sent_sms',
        }),
      }).catch(() => {});
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const convertToBooking = async () => {
    if (!quote) return;
    if (!client.name.trim()) { toast.error('Add client name first'); return; }
    if (!booking.event_date) { toast.error('Pick an event date'); return; }
    setBusy(true);
    try {
      const payload = {
        client_name: client.name,
        client_phone: client.phone,
        client_email: client.email,
        vehicle: quote.vehicle,
        vehicle_name: quote.vehicle,
        event_date: booking.event_date,
        pickup_time: booking.pickup_time,
        pickup_location: booking.pickup_location,
        dropoff_location: booking.dropoff_location,
        hours: quote.effective_hours,
        rate: quote.applied_rate,
        subtotal: quote.revenue,
        total: quote.revenue,
        notes: booking.notes,
        rate_type: quote.pricing_type,
        cc_status: 'upcoming',
        status: 'pending_payment',
        source: 'mobile_quote_convert',
      };
      const r = await fetch(`${API}/api/command-center/confirmed-bookings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('Booking creation failed');
      const d = await r.json();
      toast.success(`Booking ${d.booking_id} created`);
      // Mark quote as converted (silent)
      fetch(`${API}/api/command-center/quote-log`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: client.name, client_phone: client.phone, client_email: client.email,
          service_type: quote.vehicle, vehicle: quote.vehicle, hours: quote.effective_hours,
          subtotal: quote.revenue, total: quote.revenue, status: 'converted',
          booking_id: d.booking_id, event_date: booking.event_date,
        }),
      }).catch(() => {});
      // Reset
      setQuote(null);
      setConvertOpen(false);
      setBooking({ event_date: '', pickup_time: '', pickup_location: '', dropoff_location: '', notes: '' });
      setClient({ name: '', phone: '', email: '' });
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="mobile-quote">
      <SectionHead>Quote Builder</SectionHead>

      <Card style={{ marginBottom: 12 }}>
        <Field label="Vehicle">
          <select value={draft.vehicle_type} onChange={e => setDraft({ ...draft, vehicle_type: e.target.value })} style={inputStyle()} data-testid="quote-vehicle">
            <option value="" style={{ background: T.dark }}>— Select —</option>
            {Object.entries(rateCard).map(([k, v]) => <option key={k} value={k} style={{ background: T.dark }}>{v.name} · ${v.rate}/hr · {v.min_hours}h min</option>)}
          </select>
        </Field>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <Field label="Hours"><input type="number" min="1" value={draft.hours} onChange={e => setDraft({ ...draft, hours: Number(e.target.value) })} style={inputStyle()} data-testid="quote-hours" /></Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Type">
              <select value={draft.pricing_type} onChange={e => setDraft({ ...draft, pricing_type: e.target.value })} style={inputStyle()} data-testid="quote-type">
                <option value="retail" style={{ background: T.dark }}>Retail</option>
                <option value="member" style={{ background: T.dark }}>Member (20%)</option>
              </select>
            </Field>
          </div>
        </div>
        <button onClick={calculate} disabled={busy} style={{ background: T.red, color: '#FFFFFF', border: 0, padding: '12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', marginTop: 12, width: '100%', opacity: busy ? 0.5 : 1 }} data-testid="quote-calc">Calculate</button>
      </Card>

      {quote && (
        <>
          <Card style={{ marginBottom: 12 }} data-testid="quote-result">
            <p style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 6px' }}>Quote</p>
            <p style={{ fontSize: 16, fontWeight: 900, color: T.white, margin: '0 0 8px' }}>{quote.vehicle}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 8 }}>
              <div><p style={{ fontSize: 9, color: T.dim, margin: 0 }}>HOURS</p><p style={{ fontSize: 14, color: T.white, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{quote.effective_hours}h</p></div>
              <div><p style={{ fontSize: 9, color: T.dim, margin: 0 }}>RATE</p><p style={{ fontSize: 14, color: T.white, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{fmt$(quote.applied_rate)}/hr</p></div>
              <div><p style={{ fontSize: 9, color: T.dim, margin: 0 }}>PROFIT</p><p style={{ fontSize: 14, color: T.green, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{fmt$(quote.profit)}</p></div>
              <div><p style={{ fontSize: 9, color: T.dim, margin: 0 }}>MARGIN</p><p style={{ fontSize: 14, color: T.gold, fontFamily: 'DM Mono, monospace', fontWeight: 700, margin: 0 }}>{quote.margin_percent}%</p></div>
            </div>
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 11, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em' }}>Total</span>
              <span style={{ fontSize: 22, fontWeight: 900, color: T.gold, fontFamily: 'DM Mono, monospace' }}>{fmt$2(quote.revenue)}</span>
            </div>
          </Card>

          <Card>
            <p style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 8px' }}>Send To Client</p>
            <input value={client.name} onChange={e => setClient({ ...client, name: e.target.value })} placeholder="Client name" style={inputStyle({ marginBottom: 6 })} data-testid="quote-client-name" />
            <input value={client.phone} onChange={e => setClient({ ...client, phone: e.target.value })} placeholder="Phone (for SMS)" type="tel" inputMode="tel" style={inputStyle({ marginBottom: 6 })} data-testid="quote-client-phone" />
            <input value={client.email} onChange={e => setClient({ ...client, email: e.target.value })} placeholder="Email" type="email" inputMode="email" style={inputStyle({ marginBottom: 10 })} data-testid="quote-client-email" />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={logQuote} disabled={busy}
                style={{ flex: 1, background: T.green, color: T.black, border: 0, padding: '12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', opacity: busy ? 0.5 : 1 }}
                data-testid="quote-log"
              >Log Quote</button>
              <button
                onClick={smsQuote} disabled={busy}
                style={{ flex: 1, background: T.red, color: '#FFFFFF', border: 0, padding: '12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer', opacity: busy ? 0.5 : 1 }}
                data-testid="quote-sms"
              >SMS Client</button>
            </div>
            <button
              onClick={() => setConvertOpen(o => !o)}
              style={{ width: '100%', marginTop: 8, background: 'transparent', border: `1px solid ${T.gold}`, color: T.gold, padding: '12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              data-testid="quote-convert-toggle"
            >
              <span style={{ fontSize: 14 }}>→</span> Convert to Booking
            </button>
            <p style={{ fontSize: 9, color: T.dim, margin: '8px 0 0', textAlign: 'center', letterSpacing: '0.05em' }}>SMS sends via OpenPhone and auto-logs the quote</p>
          </Card>

          {convertOpen && (
            <Card style={{ marginTop: 12, borderColor: T.gold }} data-testid="quote-convert-panel">
              <p style={{ fontSize: 10, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 10px', fontWeight: 800 }}>New Booking · {quote.vehicle}</p>

              <Field label="Event Date *">
                <input type="date" value={booking.event_date} onChange={e => setBooking({ ...booking, event_date: e.target.value })} style={inputStyle()} data-testid="convert-event-date" />
              </Field>
              <div style={{ marginTop: 8 }}>
                <Field label="Pickup Time">
                  <input type="time" value={booking.pickup_time} onChange={e => setBooking({ ...booking, pickup_time: e.target.value })} style={inputStyle()} data-testid="convert-pickup-time" />
                </Field>
              </div>
              <div style={{ marginTop: 8 }}>
                <Field label="Pickup Location">
                  <input value={booking.pickup_location} onChange={e => setBooking({ ...booking, pickup_location: e.target.value })} placeholder="Address or hotel" style={inputStyle()} data-testid="convert-pickup-location" />
                </Field>
              </div>
              <div style={{ marginTop: 8 }}>
                <Field label="Dropoff Location">
                  <input value={booking.dropoff_location} onChange={e => setBooking({ ...booking, dropoff_location: e.target.value })} placeholder="Address (optional)" style={inputStyle()} data-testid="convert-dropoff-location" />
                </Field>
              </div>
              <div style={{ marginTop: 8 }}>
                <Field label="Notes">
                  <textarea value={booking.notes} onChange={e => setBooking({ ...booking, notes: e.target.value })} placeholder="Special requests, occasion, party size…" rows={2} style={inputStyle({ resize: 'vertical' })} data-testid="convert-notes" />
                </Field>
              </div>

              <button
                onClick={convertToBooking} disabled={busy}
                style={{ width: '100%', marginTop: 12, background: T.gold, color: T.black, border: 0, padding: '14px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', cursor: 'pointer', opacity: busy ? 0.5 : 1 }}
                data-testid="convert-confirm"
              >Confirm Booking · {fmt$2(quote.revenue)}</button>
              <p style={{ fontSize: 9, color: T.dim, margin: '6px 0 0', textAlign: 'center' }}>Syncs to Daily Ops + Google Calendar · marks quote as converted</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 5. Membership ROI — pure client-side calculator
// ──────────────────────────────────────────────────────────────────────
export function MobileROI() {
  const [tiers, setTiers] = useState({});
  const [tier, setTier] = useState('essentials');
  const [monthlyHours, setMonthlyHours] = useState(8);
  const [vehicleRate, setVehicleRate] = useState(95);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/command-center/ops/membership-tiers`);
        const d = await r.json();
        if (cancelled) return;
        setTiers(d.tiers || d);
      } catch (e) { /* gracefully fall back to empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const result = useMemo(() => {
    const t = tiers[tier] || {};
    const monthlyFee = t.monthly_fee || 0;
    const discount = (t.discount_pct || 0) / 100;
    const includedHours = t.hours_included || 0;
    const billableHours = Math.max(0, monthlyHours - includedHours);
    const retailMonthly = monthlyHours * vehicleRate;
    const memberMonthly = monthlyFee + (billableHours * vehicleRate * (1 - discount));
    const monthlySavings = retailMonthly - memberMonthly;
    const annualSavings = monthlySavings * months;
    const breakEvenMonths = monthlySavings > 0 ? monthlyFee / monthlySavings : null;
    return { tier: t, monthlyFee, discount, includedHours, retailMonthly, memberMonthly, monthlySavings, annualSavings, breakEvenMonths };
  }, [tiers, tier, monthlyHours, vehicleRate, months]);

  return (
    <div data-testid="mobile-roi">
      <SectionHead>Membership ROI</SectionHead>

      <Card style={{ marginBottom: 12 }}>
        <Field label="Membership Tier">
          <select value={tier} onChange={e => setTier(e.target.value)} style={inputStyle()} data-testid="roi-tier">
            {Object.entries(tiers).map(([key, t]) => (
              <option key={key} value={key} style={{ background: T.dark }}>{t.name || key} · {fmt$(t.monthly_fee)}/mo · {t.discount_pct || 0}% off</option>
            ))}
          </select>
        </Field>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <Field label="Hours/Month"><input type="number" min="0" value={monthlyHours} onChange={e => setMonthlyHours(Number(e.target.value))} style={inputStyle()} data-testid="roi-hours" /></Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Vehicle $/hr"><input type="number" min="0" value={vehicleRate} onChange={e => setVehicleRate(Number(e.target.value))} style={inputStyle()} data-testid="roi-rate" /></Field>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <Field label={`Period: ${months} month${months === 1 ? '' : 's'}`}>
            <input type="range" min="1" max="36" value={months} onChange={e => setMonths(Number(e.target.value))} style={{ width: '100%', accentColor: T.red }} data-testid="roi-months" />
          </Field>
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }} data-testid="roi-result">
        <p style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 8px' }}>{months}-Month Savings</p>
        <p style={{ fontSize: 28, fontWeight: 900, color: result.monthlySavings >= 0 ? T.green : T.red, fontFamily: 'DM Mono, monospace', margin: '0 0 4px' }}>{fmt$(result.annualSavings)}</p>
        <p style={{ fontSize: 11, color: T.light, margin: 0 }}>vs paying retail every booking</p>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
        <Stat label="Retail/mo" value={fmt$(result.retailMonthly)} color={T.amber} />
        <Stat label="Member/mo" value={fmt$(result.memberMonthly)} color={T.green} />
        <Stat label="Monthly Save" value={fmt$(result.monthlySavings)} color={result.monthlySavings >= 0 ? T.green : T.red} />
        <Stat label="Break-Even" value={result.breakEvenMonths ? `${result.breakEvenMonths.toFixed(1)}mo` : '—'} color={T.gold} />
      </div>

      <Card style={{ marginTop: 12 }}>
        <p style={{ fontSize: 10, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.2em', margin: '0 0 6px' }}>Tier Benefits</p>
        <p style={{ fontSize: 11, color: T.white, margin: 0, lineHeight: 1.5 }}>
          {result.tier.name || tier}: {fmt$(result.monthlyFee)}/mo · {((result.discount || 0) * 100).toFixed(0)}% off rate · {result.includedHours || 0}h included
        </p>
      </Card>
    </div>
  );
}
